import React, { useState, useEffect } from 'react';
import { ShieldCheckIcon, XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import { mfaService, MfaStatus } from '../services/mfaService';
import { MfaEnrollment } from './MfaEnrollment';
import { useAuth } from '../contexts/AuthContext';

const GRACE_PERIOD_DAYS = 7;
const GRACE_PERIOD_MS = GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000;

interface MfaNotificationBannerProps {
  onEnrollmentComplete?: () => void;
}

const getMfaBannerMessage = (isUrgent: boolean, daysRemaining: number | null): string => {
  if (isUrgent) {
    return '⚠️ Multi-factor authentication is now required';
  }
  if (daysRemaining !== null) {
    return `Multi-factor authentication required in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`;
  }
  return 'Multi-factor authentication setup required';
};

export const MfaNotificationBanner: React.FC<MfaNotificationBannerProps> = ({ onEnrollmentComplete }) => {
  const [mfaStatus, setMfaStatus] = useState<MfaStatus | null>(null);
  const [showEnrollment, setShowEnrollment] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
  const { user, token } = useAuth();

  // Reset dismissed state when user changes (new login)
  useEffect(() => {
    if (user) {
      setDismissed(false);
    }
  }, [user?.id]); // Reset when user ID changes (new login)

  useEffect(() => {
    if (!user || !token) {
      setMfaStatus(null);
      return;
    }

    const checkMfaStatus = async () => {
      try {
        const status = await mfaService.checkStatus();
        setMfaStatus(status);

        // Track grace period from first login (when MFA was first required)
        // Store the first time we detect MFA is required
        const firstMfaRequiredKey = `mfa-first-required-${user.id}`;
        const firstMfaRequired = localStorage.getItem(firstMfaRequiredKey);
        
        if (status.mfaRequired && !status.mfaEnrolled) {
          // If this is the first time we see MFA is required, store the timestamp
          if (!firstMfaRequired) {
            localStorage.setItem(firstMfaRequiredKey, Date.now().toString());
          }
          
          // Calculate days remaining from first requirement
          const firstRequiredTime = parseInt(firstMfaRequired || Date.now().toString(), 10);
          const timeSinceFirstRequired = Date.now() - firstRequiredTime;
          const remaining = Math.ceil((GRACE_PERIOD_MS - timeSinceFirstRequired) / (24 * 60 * 60 * 1000));
          setDaysRemaining(Math.max(0, remaining));
          
          // Check if grace period has expired
          const gracePeriodExpired = timeSinceFirstRequired >= GRACE_PERIOD_MS;
          
          // Check if user has dismissed for this session (sessionStorage, not localStorage)
          const dismissalKey = `mfa-dismissed-${user.id}`;
          const dismissedAt = sessionStorage.getItem(dismissalKey);
          
          if (dismissedAt && !gracePeriodExpired) {
            // User dismissed for this session, but grace period not expired - only show if < 2 days remaining
            if (remaining <= 2) {
              setDismissed(false);
            } else {
              setDismissed(true);
            }
          } else {
            // Not dismissed or grace period expired - show banner
            setDismissed(false);
          }
        } else {
          // MFA not required or already enrolled - clear tracking
          localStorage.removeItem(firstMfaRequiredKey);
          sessionStorage.removeItem(`mfa-dismissed-${user.id}`);
          setDismissed(true);
        }

        // Check if user has enrolled
        if (status.mfaEnrolled) {
          setDismissed(true);
          // Clear tracking when enrolled
          localStorage.removeItem(firstMfaRequiredKey);
          sessionStorage.removeItem(`mfa-dismissed-${user.id}`);
        }
      } catch (error) {
        console.error('[MfaNotificationBanner] Failed to check MFA status:', error);
        console.error('[MfaNotificationBanner] Error details:', error);
        // Don't show banner if we can't check status
        setMfaStatus(null);
      }
    };

    checkMfaStatus();
    
    // Re-check every 5 minutes in case status changes
    const interval = setInterval(checkMfaStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, token]);

  const handleDismiss = () => {
    if (user) {
      // Use sessionStorage so dismissal only lasts for this session
      const dismissalKey = `mfa-dismissed-${user.id}`;
      sessionStorage.setItem(dismissalKey, Date.now().toString());
      setDismissed(true);
      
      // Recalculate days remaining
      const firstMfaRequiredKey = `mfa-first-required-${user.id}`;
      const firstMfaRequired = localStorage.getItem(firstMfaRequiredKey);
      if (firstMfaRequired) {
        const firstRequiredTime = parseInt(firstMfaRequired, 10);
        const timeSinceFirstRequired = Date.now() - firstRequiredTime;
        const remaining = Math.ceil((GRACE_PERIOD_MS - timeSinceFirstRequired) / (24 * 60 * 60 * 1000));
        setDaysRemaining(Math.max(0, remaining));
      }
    }
  };

  const handleEnrollmentComplete = async () => {
    setShowEnrollment(false);
    // Refresh MFA status
    try {
      const status = await mfaService.checkStatus();
      setMfaStatus(status);
      if (status.mfaEnrolled) {
        setDismissed(true);
        onEnrollmentComplete?.();
      }
    } catch (error) {
      console.error('Failed to refresh MFA status:', error);
    }
  };

  // Don't show if:
  // - No MFA status yet (still loading)
  // - User doesn't need MFA
  // - User has enrolled
  // - User has dismissed and grace period not urgent
  if (!mfaStatus || !mfaStatus.mfaRequired || mfaStatus.mfaEnrolled || dismissed) {
    return null;
  }

  // Calculate if urgent (grace period expired)
  const firstMfaRequiredKey = `mfa-first-required-${user?.id}`;
  const firstMfaRequired = localStorage.getItem(firstMfaRequiredKey);
  let isUrgent = false;
  
  if (firstMfaRequired) {
    const firstRequiredTime = parseInt(firstMfaRequired, 10);
    const timeSinceFirstRequired = Date.now() - firstRequiredTime;
    isUrgent = timeSinceFirstRequired >= GRACE_PERIOD_MS;
    
    if (!isUrgent && daysRemaining === null) {
      const remaining = Math.ceil((GRACE_PERIOD_MS - timeSinceFirstRequired) / (24 * 60 * 60 * 1000));
      setDaysRemaining(Math.max(0, remaining));
    }
  } else {
    // First time seeing MFA required - set initial grace period
    setDaysRemaining(GRACE_PERIOD_DAYS);
  }

  if (showEnrollment) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <MfaEnrollment 
            onComplete={handleEnrollmentComplete}
            onDismiss={() => setShowEnrollment(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-r ${isUrgent ? 'from-red-500 to-red-600' : 'from-yellow-400 to-yellow-500'} text-white shadow-lg relative z-50 flex-shrink-0`}>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-5 w-5 mr-2" />
            <div>
              <p className="font-medium">
                {getMfaBannerMessage(isUrgent, daysRemaining)}
              </p>
              <p className="text-sm opacity-90">
                {isUrgent
                  ? 'Your grace period has expired. Please set up MFA to continue using the platform.'
                  : 'Please set up multi-factor authentication for enhanced security. You have a 7-day grace period.'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowEnrollment(true)}
              className="px-4 py-2 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors"
            >
              Set Up Now
            </button>
            {!isUrgent && (
              <button
                onClick={handleDismiss}
                className="text-white hover:text-gray-200 transition-colors"
                title="Remind me later"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
